<script module>
  import AppLayout from '$lib/layouts/AppLayout.svelte'
  export const layout = AppLayout
</script>

<script lang="ts">
  import { router } from "@inertiajs/svelte";
  import { Plus, FileText, Edit3, Trash2, Search, Eye } from "lucide-svelte";
  import { appStore } from '$lib/stores/appStore';

  let { pages = [], site_info = {}, currentLocale, current_site = {}, t = {} as any } = $props();

  // 如果 current_site 内部没带 primary_domain，回退到 site_info 的配置
  let primaryDomain = $derived(current_site?.primary_domain || site_info?.primary_domain || "");

  function deletePage(id: string) {
    if (!confirm(t.btn_delete_confirm)) return;
    router.delete(`/pages/${id}`);
  }

  $effect(() => {
    const title = t.title;
    appStore.setTitle(title);
  });
</script>

<div class="p-8 max-w-7xl mx-auto">
  <div class="flex justify-between items-end mb-8">
    <div>
      <h1 class="text-3xl font-bold tracking-tight text-zinc-900 dark:text-white">{t.title}</h1>
      <p class="text-muted-foreground mt-1 text-sm">
        {t.current_site}<span class="text-primary font-bold uppercase">{current_site?.name}</span> 
        {t.preview_lang}<span class="text-primary font-bold uppercase">{currentLocale}</span>
      </p>
    </div>

    <div class="flex items-center gap-4">
      <div class="flex bg-zinc-100 dark:bg-zinc-800 p-1 rounded-xl border border-zinc-200 dark:border-zinc-700">
        {#each site_info?.supported_locales || [] as loc}
          <button 
            onclick={() => router.get('/pages', { edit_locale: loc }, { preserveState: true })}
            class="px-4 py-1.5 rounded-lg text-xs font-bold transition-all {currentLocale === loc ? 'bg-white dark:bg-zinc-700 shadow-sm text-primary' : 'text-zinc-500 hover:text-zinc-700'}"
          >
            {loc.toUpperCase()}
          </button>
        {/each}
      </div>

      <button 
        onclick={() => router.get('/pages/new', { edit_locale: currentLocale })}
        class="flex items-center gap-2 px-5 py-2.5 bg-zinc-900 dark:bg-white dark:text-black text-white rounded-xl font-bold shadow-sm hover:opacity-90 transition-opacity"
      >
        <Plus class="size-4" /> {t.btn_new}
      </button>
    </div>
  </div>

  <div class="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-2xl overflow-hidden shadow-sm">
    <table class="w-full text-left border-collapse">
      <thead>
        <tr class="border-b bg-zinc-50/50 dark:bg-zinc-800/50">
          <th class="p-4 text-xs font-bold uppercase text-zinc-500">{t.table?.th_title}</th>
          <th class="p-4 text-xs font-bold uppercase text-zinc-500">{t.table?.th_status}</th>
          <th class="p-4 text-xs font-bold uppercase text-zinc-500">{t.table?.th_mode}</th>
          <th class="p-4 text-xs font-bold uppercase text-zinc-500 text-right">{t.table?.th_actions}</th>
        </tr>
      </thead>
      <tbody class="divide-y divide-zinc-100 dark:divide-zinc-800">
        {#each pages as page (page.id)}
          <tr class="hover:bg-zinc-50/50 dark:hover:bg-zinc-800/30 transition-colors">
            <td class="p-4">
              <div class="flex items-center gap-3">
                <FileText class="size-4 text-zinc-400" />
                <div>
                  <div class="font-bold text-sm text-zinc-900 dark:text-zinc-100">{page.title || t.no_name_page}</div>
                  <div class="text-xs text-zinc-400 font-mono">/{page.slug}</div>
                </div>
              </div>
            </td>
            <td class="p-4">
              {#if page.is_translated}
                <span class="inline-flex items-center gap-1 text-[10px] font-bold text-green-600 bg-green-50 dark:bg-green-900/20 px-2 py-0.5 rounded">{t.status_ready}</span>
              {:else}
                <span class="inline-flex items-center gap-1 text-[10px] font-bold text-zinc-400 bg-zinc-100 dark:bg-zinc-800 px-2 py-0.5 rounded">{t.status_untranslated}</span>
              {/if}
            </td>
            <td class="p-4 text-xs font-medium">
              {#if page.mode === 'components'}
                <span class="flex items-center gap-1.5 text-blue-600">{t.mode_components}</span>
              {:else if page.mode === 'code'}
                <span class="flex items-center gap-1.5 text-amber-600">{t.mode_code}</span>
              {:else}
                <span class="flex items-center gap-1.5 text-zinc-400 italic">{t.mode_default}</span>
              {/if}
            </td>
            <td class="p-4 text-right">
              <div class="flex items-center justify-end gap-1">
                {#if page.slug}
                  {@const previewUrl = primaryDomain 
                    ? (primaryDomain.startsWith('http') ? `${primaryDomain}/${page.slug}` : `https://${primaryDomain}/${page.slug}`)
                    : `/${page.slug}`}

                  <a 
                    href={previewUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    class="p-2 hover:bg-zinc-100 dark:hover:bg-zinc-800 rounded-lg text-zinc-600 dark:text-zinc-400 inline-flex items-center justify-center"
                    title={t.preview}
                  >
                    <Eye class="size-4" />
                  </a>
                {/if}
                <button 
                  onclick={() => router.get(`/pages/${page.id}/edit`, { edit_locale: currentLocale })} 
                  class="p-2 hover:bg-zinc-100 dark:hover:bg-zinc-800 rounded-lg text-zinc-600 dark:text-zinc-400"
                >
                  <Edit3 class="size-4" />
                </button>
                <button 
                  onclick={() => deletePage(page.id)} 
                  class="p-2 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg text-red-500"
                >
                  <Trash2 class="size-4" />
                </button>
              </div>
            </td>
          </tr>
        {:else}
          <tr>
            <td colspan="4" class="p-12 text-center">
              <div class="flex flex-col items-center gap-2 text-zinc-400">
                <Search class="size-8 opacity-20" />
                <p class="text-sm italic">{t.empty_placeholder}</p>
              </div>
            </td>
          </tr>
        {/each}
      </tbody>
    </table>
  </div>
</div>